import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Navbar from '../components/public/Navbar';
import Carousel from '../components/public/Carousel';
import Footer from '../components/public/Footer';
import { FileText, Search, Users, BarChart3, Megaphone, Download, ChevronDown, ChevronUp } from 'lucide-react';

const Home = () => {
  const [circulars, setCirculars] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState(null);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    axios.get(`${apiBase}/announcements/public?limit=5`)
      .then(res => setCirculars(res.data.announcements || []))
      .catch(() => {});
  }, []);

  const handleDocDownload = (signedUrl) => {
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    } else {
      toast.error('Download link unavailable, please refresh the page');
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  // Carousel slides data
  const slides = [
    {
      image: 'https://cdn.s3waas.gov.in/s368264bdb65b97eeae6788aa3348e553c/uploads/2018/04/2018042786.jpg',
      title: 'Welcome to Mon District Land Permit Portal',
      description: 'Digital Land Record Management System for Mon District, Nagaland'
    },
    {
      image: 'https://cdn.s3waas.gov.in/s368264bdb65b97eeae6788aa3348e553c/uploads/2020/05/2020050884.jpg',
      title: 'The Land of Anghs',
      description: 'Preserving heritage while embracing digital transformation'
    }
  ];

  // Services offered
  const services = [
    {
      icon: FileText,
      title: 'Apply for Land Permit',
      description: 'Submit new land permit applications online with required documents',
      link: '/login',
      color: 'blue'
    },
    {
      icon: Search,
      title: 'Search Permits',
      description: 'Search and view approved land permits by permit number',
      link: '/search',
      color: 'green'
    },
    {
      icon: BarChart3,
      title: 'Track Application',
      description: 'Monitor the status of your permit application in real-time',
      link: '/login',
      color: 'purple'
    },
    {
      icon: Users,
      title: 'Portal Login',
      description: 'Access the portal for officers and administrators',
      link: '/login',
      color: 'orange'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Carousel Section */}
      <Carousel slides={slides} autoPlayInterval={5000} />

      {/* Main Content */}
      <main className="flex-grow">
        {/* Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Access various land permit services through our digital portal
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className={`inline-flex p-3 rounded-lg ${getColorClasses(service.color)} mb-4`}>
                    <service.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Circulars Section */}
        {circulars.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 text-blue-600 mb-3">
                  <Megaphone className="h-6 w-6" />
                  <span className="text-sm font-semibold uppercase tracking-wide">Official Notices</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Latest Circulars</h2>
              </div>

              <div className="max-w-3xl mx-auto space-y-4">
                {circulars.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-gray-600 line-clamp-3">{item.message}</p>

                        {item.documents && item.documents.length > 0 && (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedDocs(expandedDocs === item.id ? null : item.id)}
                              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              <Download className="h-3.5 w-3.5" />
                              {item.documents.length} Attachment{item.documents.length > 1 ? 's' : ''}
                              {expandedDocs === item.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                            {expandedDocs === item.id && (
                              <div className="mt-1 space-y-1">
                                {item.documents.map((doc, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => handleDocDownload(doc.signedUrl)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 py-0.5 w-full text-left"
                                  >
                                    <Download className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">{doc.originalName}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-6">
                <Link
                  to="/circulars"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-blue-600 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors"
                >
                  View All Circulars
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* About Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  About Mon Land Permit Portal
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    The Mon District Land Permit Portal is a comprehensive digital platform designed to
                    streamline the land permit application and approval process in Mon District, Nagaland.
                  </p>
                  <p>
                    Our system provides a transparent, efficient, and user-friendly interface for citizens
                    to apply for land permits, track their applications, and access approved permit records.
                  </p>
                  <p>
                    The portal implements a multi-level approval workflow ensuring proper verification and
                    authorization at each stage, from branch users to officers and final approval by DC/ADC.
                  </p>
                </div>
                <div className="mt-8">
                  <Link
                    to="/about"
                    className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h3>
                <ul className="space-y-4">
                  {[
                    'Digital permit management system',
                    'Multi-level approval workflow',
                    'Role-based access control',
                    'Document upload support',
                    'Real-time status tracking',
                    'Public search facility',
                    'Comprehensive audit trail',
                    'Reports and analytics'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="bg-blue-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Access our digital land permit portal to apply for new permits or search existing records
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
              >
                Login to Portal
              </Link>
              <Link
                to="/search"
                className="inline-block bg-blue-700 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-800 transition-colors border-2 border-white"
              >
                Search Permits
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
